import React, { useState } from 'react';
import { Tree, Button, Modal, Form, Input, Select, Space, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useCategories } from '../hooks/useCategories';

const { Option } = Select;

function CategoriesManager({ visible, onClose }) {
  const { categoriesTree, categories, loading, createCategory, updateCategory, deleteCategory, getFlatCategories } = useCategories();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    let success;
    if (editingCategory) {
      success = await updateCategory(editingCategory.id, values);
    } else {
      success = await createCategory(values);
    }
    
    if (success) {
      setIsModalVisible(false);
      setEditingCategory(null);
      form.resetFields();
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      category_name: category.category_name,
      parent_id: category.parent_id
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (categoryId) => {
    await deleteCategory(categoryId);
  };

  const renderTreeNodes = (data) => {
    return data.map(item => ({
      title: (
        <div className="flex items-center justify-between group">
          <span className="flex items-center">
            <FolderOutlined className="mr-2 text-blue-500" />
            {item.category_name}
          </span>
          <Space size="small" className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(item);
              }}
            />
            <Popconfirm
              title="Ești sigur că vrei să ștergi această categorie?"
              onConfirm={(e) => {
                e.stopPropagation();
                handleDelete(item.id);
              }}
              okText="Da"
              cancelText="Nu"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Space>
        </div>
      ),
      key: item.id,
      children: item.children && item.children.length > 0 ? renderTreeNodes(item.children) : undefined,
    }));
  };

  const flatCategories = getFlatCategories();

  return (
    <Modal
      title="Gestionare Categorii"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <div className="mb-4">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCategory(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Adaugă Categorie
        </Button>
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-auto">
        {categoriesTree.length > 0 ? (
          <Tree
            treeData={renderTreeNodes(categoriesTree)}
            defaultExpandAll
            showIcon
            className="bg-transparent"
          />
        ) : (
          <div className="text-center text-gray-500 py-8">
            Nu există categorii create încă
          </div>
        )}
      </div>

      <Modal
        title={editingCategory ? 'Editează Categoria' : 'Adaugă Categorie Nouă'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="category_name"
            label="Numele categoriei"
            rules={[{ required: true, message: 'Numele categoriei este obligatoriu' }]}
          >
            <Input placeholder="Introduceți numele categoriei" />
          </Form.Item>

          <Form.Item
            name="parent_id"
            label="Categoria părinte"
          >
            <Select
              placeholder="Selectează categoria părinte (opțional)"
              allowClear
            >
              {flatCategories
                .filter(cat => !editingCategory || cat.id !== editingCategory.id)
                .map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.displayName}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <div className="flex justify-end space-x-2 pt-4">
            <Button onClick={() => {
              setIsModalVisible(false);
              setEditingCategory(null);
              form.resetFields();
            }}>
              Anulează
            </Button>
            <Button type="primary" htmlType="submit" className="bg-blue-600 hover:bg-blue-700">
              {editingCategory ? 'Actualizează' : 'Creează'} Categoria
            </Button>
          </div>
        </Form>
      </Modal>
    </Modal>
  );
}

export default CategoriesManager; 